import { PageHeader, PageTabs } from '@/shared/ui/components';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Ban, Menu, Settings, Calendar as CalendarIcon, Cpu } from 'lucide-react';
import { SpecialtyFilter } from '@/components/cards';

/**
 * CalendarToolbar — Block
 * Top header for the scheduling view.
 *
 * Row 1: tabs · search · practice selector · settings · create
 * Row 2: specialty filter chips (full-width)
 *
 * Date picker, status filter pills, and view toggle live in the
 * `CalendarSubToolbar` block (rendered below the provider strip).
 */

type SchedulingTab =
  | 'calendar'
  | 'fuRequests'
  | 'orderRequests'
  | 'referrals'
  | 'technicianWorklist';

interface CalendarToolbarProps {
  activeTab: SchedulingTab;
  onTabChange: (tab: SchedulingTab) => void;
  /**
   * Static fallback label for the practice chip. Used when no
   * `practiceSwitcher` slot is provided (e.g. the Storybook story).
   * The live app passes the interactive `practiceSwitcher` instead.
   */
  practiceName: string;
  /**
   * Interactive practice dropdown (the `<PracticeSwitcher />` organism,
   * wired to the auth store). When provided it replaces the static
   * `practiceName` chip. Kept as a slot so this presentational block
   * doesn't take a dependency on the auth store / practices service —
   * the scheduling frame composes the switcher and passes it in.
   */
  practiceSwitcher?: ReactNode;
  /**
   * Interactive appointment search (the `<AppointmentSearch />` typeahead,
   * wired to `useAppointments` + navigation). When provided it replaces the
   * static search input. Slot-injected so this presentational block keeps no
   * dependency on the scheduling services / router — the frame composes it.
   */
  searchSlot?: ReactNode;
  /**
   * Scheduling-AI command bar (the `<SchedulingAiCommandBar />` wired to
   * the stub parser + create-rail prefill). Slot-injected like
   * `searchSlot` so this presentational block keeps no dependency on the
   * AI module or the feature flag — the scheduling frame checks
   * `VITE_SCHEDULING_AI` (fail-closed) and only passes the node when the
   * flag is on. Absent → the row does not render at all.
   */
  aiCommandSlot?: ReactNode;
  /**
   * Practitioners / Devices tab strip (PROD-2007, `<CalendarActorTabs />`).
   * Slot-injected so this block keeps no dependency on the actor state; the
   * scheduling frame composes it. Absent -> no row renders (today's calendar
   * is byte-identical).
   */
  actorTabs?: ReactNode;
  /** The practitioner filter panel, opened from the Practitioners button: drawn over the page, under the actor bar. */
  actorPanel?: ReactNode;
  /** The far right of the Practitioners / Devices row: the calendar's view switch (Antonin, 13 Oct). */
  actorCenter?: ReactNode;
  actorTrailing?: ReactNode;
  /**
   * Replaces the specialty-chip filter row while the Devices tab is active
   * (the subtype filter, PROD-2007). Absent -> the specialty row + provider
   * bulk-select menu render exactly as before.
   */
  filterRow?: ReactNode;
  /**
   * Draw the PRACTITIONER filter row — the specialty chips plus the trailing
   * provider bulk-select menu. False removes the whole row.
   *
   * `filterRow` cannot express this on its own: an absent slot means "draw the
   * practitioner row", which is how that row outlived the subtype filter being
   * hidden on the Devices tab. Andrew, 2026-09-14, looking at the live tab: a
   * device has no specialty ("no speciality for the device and the technician",
   * 2026-09-13), so the chips were narrowing MACHINES by practitioner
   * specialties; and the ≡ menu beside them drives the provider checkboxes,
   * which a device card never draws (`onToggleProvider` is undefined there),
   * so it had nothing to toggle either. Default true — no other caller changes.
   */
  showSpecialtyFilterRow?: boolean;
  /**
   * "Book a device" item in the Create dropdown (PROD-2007). Only passed while
   * the Devices tab is active; absent -> the menu is unchanged.
   */
  onBookDeviceClick?: () => void;
  /**
   * "Block a device" item in the Create dropdown (PROD-2454) — the Devices
   * tab's counterpart to "Block Availability", opening the same panel for a
   * device actor.
   *
   * Presence-gated like `onBookDeviceClick` and NOT like
   * `canBlockAvailability`, on purpose: the practitioner item's boolean
   * exists because its handler is always wired and only the permission
   * varies (Samer review §2, fail-closed). This one is the opposite shape —
   * the caller passes it only on the Devices tab and only for a user who may
   * block, so absence is the gate and there is no second switch to fall out
   * of sync with it.
   */
  onBlockDeviceClick?: () => void;
  /**
   * Fires when "New Appointment" is selected from the Create dropdown.
   * Flutter parity: `calendar_screen.dart:3155` — opens the
   * CreateAppointmentPanel on the left rail.
   */
  onCreateClick: () => void;
  /**
   * Fires when "Block Availability" is selected from the Create dropdown.
   * Flutter parity: `calendar_screen.dart:3172` — opens the
   * BlockAvailabilityWidget on the left rail. Always provide a handler;
   * `canBlockAvailability` (NOT the absence of this prop) gates whether
   * the menu item renders. Samer review §2 — fail-closed semantics
   * shouldn't ride on whether a callback was passed.
   */
  onCreateBlockClick: () => void;
  /**
   * Show "New Appointment" in the dropdown. Maps to Flutter's
   * `Manage_Patient_Appointments` (add) permission check at
   * `calendar_screen.dart:2611-2615`. **Default: false (fail-closed)** —
   * Samer review §1: if the permission fetch is slow or errors out,
   * users without the perm must NOT briefly see the option. Parents
   * MUST explicitly flip to true once the permission is loaded.
   */
  canCreateAppointment?: boolean;
  /**
   * Show "Block Availability" in the dropdown. Maps to Flutter's
   * `Doctor_Calender` (add) permission check at `calendar_screen.dart:2637-2641`.
   * **Default: false (fail-closed)** — same reasoning as
   * `canCreateAppointment`. Explicit boolean keeps the contract
   * refactor-proof; an accidental no-op handler can't sneak the
   * option into view.
   */
  canBlockAvailability?: boolean;
  /**
   * Opens the active practice's settings page. Flutter parity:
   * `calendar_screen.dart:2806-2833` (`settingsIcon`) — the gear next to
   * the practice dropdown does `Navigator.push(PracticeBaseWidget(...))`
   * for `selectedPractice`. In React that's `/settings/practices/{id}`.
   */
  onSettingsClick?: () => void;
  /**
   * Show the settings gear. Flutter hides it entirely for Phlebotomists
   * (`calendar_screen.dart:2808` — `isPhlebotomistRole`). Default true.
   */
  showSettings?: boolean;
  fuRequestCount?: number;
  /**
   * Show the "Order Requests" tab pill (Phlebotomist-only Lab linkage
   * surface). Maps to Flutter `userData.isPhlebotomistRole` —
   * `tenantRoleId === '19'`. Default: false (fail-closed).
   */
  showOrderRequestsTab?: boolean;
  /** Show the "Referrals" tab pill (PROD-1395, `?x=referrals`). Default: false, so today's tabs are unchanged. */
  showReferralsTab?: boolean;
  orderRequestsCount?: number;
  /**
   * Show the "Device Sessions" tab pill — the Technician Worklist
   * (PROD-2007). Gated by the frame on the org-wide
   * `VITE_TECHNICIAN_WORKLIST_ENABLED` flag AND `canSeeTechnicianWorklist`
   * (tenant role '21' OR OrgAdmin). Default: false (fail-closed), same as
   * `showOrderRequestsTab` — a caller that has not thought about the gate
   * gets nothing rather than everything.
   *
   * Deliberately has NO count sibling. FU Requests and Order Requests each
   * badge a number the parent already holds; the day's device sessions are
   * not on hand anywhere in this tree, so a badge here would mean firing the
   * worklist's own fetch from the toolbar just to draw a digit — for every
   * user with the gate open, on every calendar render, including the ones
   * who never click the tab. That is a network call and a cache entry of
   * patient-linked data bought for one character of chrome. Not worth it.
   */
  showTechnicianWorklistTab?: boolean;
  /**
   * Role confinement (PROD-2396). When set, the tab strip collapses to this
   * ONE pill and every other tab is filtered out — the confined user is not
   * offered a surface their role has no business on. Undefined (the default,
   * and what every existing caller passes) leaves the strip exactly as it
   * was.
   *
   * Composes with the gates above rather than overriding them: the named tab
   * still has to survive its own `show*` check, so confining someone to a
   * tab that is itself gated off yields an EMPTY strip, not a smuggled pill.
   * That ordering is the fail-closed one and is asserted in
   * `frame-scheduling.technician-confinement.test.tsx`.
   *
   * Set by `frame-scheduling` for a Technician, pointing at
   * `'technicianWorklist'`. This hides tab CHROME; it protects no data — see
   * `@/app/lib/nav-confinement.ts` and PROD-2388.
   */
  confinedToTabId?: SchedulingTab;
  specialties: string[];
  selectedSpecialties: string[];
  onSpecialtyToggle: (s: string) => void;
  onSpecialtyClear: () => void;
  /** Bulk-select every visible provider (the ≡ menu in Flutter PM). */
  onProvidersSelectAll: () => void;
  /** Clear every provider checkbox. */
  onProvidersUnselectAll: () => void;
  /** True when every visible provider is already selected — disables "Select all". */
  allProvidersSelected?: boolean;
  /** True when no provider is selected — disables "Unselect all". */
  noProvidersSelected?: boolean;
}

type TabCountKey = 'fuRequestCount' | 'orderRequestsCount';

// E.Requests + Walk In removed 2026-05-21 — never wired beyond
// placeholder bodies; Andrew confirmed cut them rather than carry
// noise in the toolbar. If they come back, restore from git.
const ALL_TABS: { id: SchedulingTab; label: string; countKey?: TabCountKey }[] = [
  { id: 'calendar', label: 'Calendar' },
  { id: 'fuRequests', label: 'FU Requests', countKey: 'fuRequestCount' },
  // Phlebotomist + OrgAdmin only. Filtered into TABS at render time
  // based on `showOrderRequestsTab`.
  { id: 'orderRequests', label: 'Order Requests', countKey: 'orderRequestsCount' },
  // Technician + OrgAdmin only, behind VITE_TECHNICIAN_WORKLIST_ENABLED
  // (PROD-2007). Filtered into TABS at render time based on
  // `showTechnicianWorklistTab`. No `countKey` — see the prop's doc-comment:
  // the count would cost a fetch nothing else in this tree needs.
  { id: 'technicianWorklist', label: 'Device Sessions' },
  // Behind the `referrals` practice switch (PROD-1395). Filtered in TABS on `showReferralsTab`.
  { id: 'referrals', label: 'Referrals' },
];

export function CalendarToolbar({
  activeTab,
  onTabChange,
  practiceName,
  practiceSwitcher,
  searchSlot,
  aiCommandSlot,
  actorTabs,

  actorCenter,
  actorTrailing,
  filterRow,
  showSpecialtyFilterRow = true,
  onBookDeviceClick,
  onBlockDeviceClick,
  onCreateClick,
  onCreateBlockClick,
  canCreateAppointment = false,
  canBlockAvailability = false,
  onSettingsClick,
  showSettings = true,
  fuRequestCount = 0,
  showOrderRequestsTab = false,
  showReferralsTab = false,
  orderRequestsCount = 0,
  showTechnicianWorklistTab = false,
  confinedToTabId,
  specialties,
  selectedSpecialties,
  onSpecialtyToggle,
  onSpecialtyClear,
  onProvidersSelectAll,
  onProvidersUnselectAll,
  allProvidersSelected = false,
  noProvidersSelected = false,
}: CalendarToolbarProps) {
  const counts = {
    fuRequestCount,
    orderRequestsCount,
  };
  // Gated pills are REMOVED from the array, not styled away — a filtered tab
  // has no element in the DOM at all, so there is nothing to unhide from
  // DevTools. Both entries are hidden by default; only the gated consumer
  // (frame-scheduling) flips them, for `tenantRoleId === '19'` (Order
  // Requests) and for the flag + `canSeeTechnicianWorklist` (Device
  // Sessions).
  // Role confinement (PROD-2396) is applied LAST and only narrows: a confined
  // tab must already have passed its own gate above to survive. So an
  // un-gated `confinedToTabId` produces no pills at all rather than a pill
  // the gates had removed.
  const TABS = ALL_TABS.filter(
    (t) =>
      (t.id !== 'orderRequests' || showOrderRequestsTab) &&
      (t.id !== 'technicianWorklist' || showTechnicianWorklistTab) &&
      (t.id !== 'referrals' || showReferralsTab) &&
      (confinedToTabId === undefined || t.id === confinedToTabId),
  );
  // A strip holding one tab is a control that cannot navigate — the user is
  // already on the only thing it offers. Andrew 2026-09-20: "a one-tab
  // tab-bar is UI noise". So it is dropped rather than drawn as a lone pill.
  //
  // Written as a length test, not as `confinedToTabId !== undefined`, so the
  // rule is about what the strip can DO rather than about who is looking at
  // it. Nothing else changes: `calendar` and `fuRequests` are both ungated,
  // so an unconfined user always keeps at least two.
  const showTabStrip = TABS.length > 1;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!createMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) {
        setCreateMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [createMenuOpen]);

  // Flutter parity (`calendar_screen.dart:3141-3151`): the Create
  // button only renders if EITHER permission is held. When only one
  // is available, the dropdown reduces to a single item (matches
  // Flutter's PopupMenuButton, which still shows the popup with one
  // entry rather than degrading to a direct-action button).
  // Samer review §2: gate on explicit boolean props, not on whether
  // a handler was passed — an accidental no-op `() => {}` must not
  // sneak the option into view.
  const showCreateButton =
    canCreateAppointment || canBlockAvailability || !!onBookDeviceClick || !!onBlockDeviceClick;

  return (
    <div className="flex flex-col">
      {/* Top bar */}
      {/* The one page header (PageHeader + PageTabs, every practice page; Antonin 12 Oct): "Calendar" as the title at
          the practitioner app's spot, the toolbar's actions on the right, the scheduling tabs under it. */}
      <PageHeader
        className="bg-[var(--surface-read)]"
        title="Calendar"
        // The practice selector sits right after "Calendar", in its own look (Antonin, 13 Oct: "next to calendar, not
        // under", the previous selector component: practice avatar, name, chevron).
        tag={practiceSwitcher ?? <span className="truncate text-sm font-medium text-text-primary">{practiceName}</span>}
        actions={<div className="flex items-center gap-3">

        {/*
          * Search. On the Calendar tab it sits in the date row (actorTrailing); the other tabs have no date row, so it
          * stays here in the header actions, as on main. A confined role (PROD-2396) gets none: the search navigates to
          * the appointment screen, a second screen the route guard would bounce.
          */}
        {confinedToTabId === undefined && searchSlot && !(actorTabs && activeTab === 'calendar') ? searchSlot : null}

        {showSettings && (
          <button
            type="button"
            onClick={onSettingsClick}
            aria-label="Practice settings"
            className="flex items-center justify-center rounded-md p-1 text-text-muted hover:bg-grey-100 hover:text-text-primary"
          >
            <Settings className="h-4 w-4" />
          </button>
        )}

        {showCreateButton && (
          <div className="relative" ref={createMenuRef}>
            <button
              onClick={() => setCreateMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={createMenuOpen}
              className="h-8 px-4 rounded-card bg-brand-primary text-white text-sm font-medium hover:bg-interactive-hover"
            >
              Create
            </button>
            {createMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 z-50 min-w-[200px] bg-[var(--surface-read)] border border-[var(--surface-line)] rounded-card shadow-overlay py-1 text-sm"
              >
                {canCreateAppointment && (
                  <button
                    role="menuitem"
                    onClick={() => {
                      setCreateMenuOpen(false);
                      onCreateClick();
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-text-primary hover:bg-grey-100"
                  >
                    <span>New Appointment</span>
                    <CalendarIcon className="h-4 w-4 text-text-muted" />
                  </button>
                )}
                {canBlockAvailability && (
                  <button
                    role="menuitem"
                    onClick={() => {
                      setCreateMenuOpen(false);
                      onCreateBlockClick();
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-text-primary hover:bg-grey-100"
                  >
                    <span>Block Availability</span>
                    <Ban className="h-4 w-4 text-text-muted" />
                  </button>
                )}
                {onBookDeviceClick && (
                  <button
                    role="menuitem"
                    onClick={() => {
                      setCreateMenuOpen(false);
                      onBookDeviceClick();
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-text-primary hover:bg-grey-100"
                  >
                    <span>Book a device</span>
                    <Cpu className="h-4 w-4 text-text-muted" />
                  </button>
                )}
                {/* PROD-2454 — the Devices tab's Block entry point, sitting
                    where "Block Availability" sits on the Practitioners tab
                    (directly under the create item) so the same flow starts
                    from the same place on either tab. Same Ban icon for the
                    same reason: it is the same action on a different actor. */}
                {onBlockDeviceClick && (
                  <button
                    role="menuitem"
                    onClick={() => {
                      setCreateMenuOpen(false);
                      onBlockDeviceClick();
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-text-primary hover:bg-grey-100"
                  >
                    <span>Block a device</span>
                    <Ban className="h-4 w-4 text-text-muted" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
</div>}
        tabs={showTabStrip ? (
          <PageTabs ariaLabel="Scheduling" active={activeTab} onChange={(id) => onTabChange(id as SchedulingTab)}
            tabs={TABS.map((tab) => ({ id: tab.id, label: tab.label, count: tab.countKey ? counts[tab.countKey] : undefined, urgent: tab.id === 'fuRequests' }))} />
        ) : undefined}
      />

      {/* Practitioners / Devices tabs (PROD-2007) — only while the calendar
          tab is active; the FU / Order Requests surfaces have no actor. */}
      {actorTabs && activeTab === 'calendar' ? (
        <div data-calendar-date-row className="relative z-30 flex min-h-10 flex-wrap items-center gap-y-1 py-1 bg-[var(--surface-read)] border-b border-[var(--surface-line)] px-4">
          {/* In the flow, centred in the room between the dates and the search: absolutely centred, it slid under the
              search once the booking panel narrowed the row ("Practitic…", 15 Oct). When even that can't fit (booking
              panel open under ~900 px), the row takes a second line instead of overlapping; the search folds first. */}
          <div className="shrink-0">{actorTabs}</div>
          <div className="flex min-w-max flex-1 justify-center px-4">{actorCenter}</div>
          {actorTrailing ? <div className="ml-auto flex shrink-0 items-center">{actorTrailing}</div> : null}
        </div>
      ) : null}

      {/* Scheduling-AI command bar row (VITE_SCHEDULING_AI, fail-closed) —
          only exists when the frame passes the slot. Its suggestion list
          renders in-flow below the input, so it gets its own row rather
          than squeezing into the fixed-height header above. */}
      {aiCommandSlot ? (
        <div className="bg-[var(--surface-read)] border-b border-[var(--surface-line)] px-4 py-2">
          {aiCommandSlot}
        </div>
      ) : null}

      {/* Specialty filter row + trailing provider bulk-select menu
          (Flutter parity). Hidden on FU Requests / Order Requests tabs
          — those views have no provider strip + no per-specialty
          filtering, so the row is irrelevant there. */}
      {activeTab === 'calendar' && filterRow ? (
        <div className="bg-[var(--surface-read)] border-b border-[var(--surface-line)] px-4 py-2 flex items-center gap-3">
          <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide">{filterRow}</div>
        </div>
      ) : null}
      {activeTab === 'calendar' && !filterRow && showSpecialtyFilterRow && (
        <div className="bg-[var(--surface-read)] border-b border-[var(--surface-line)] px-4 py-2 flex items-center gap-3">
          <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide">
            <SpecialtyFilter
              specialties={specialties}
              selected={selectedSpecialties}
              onToggle={onSpecialtyToggle}
              onClear={onSpecialtyClear}
            />
          </div>
          {/* Provider bulk-select menu (Flutter parity — ≡ icon at top right
              opens "Select all" / "Unselect all" for the provider checkboxes,
              not the specialty chips). */}
          <div className="relative flex-shrink-0" ref={menuRef}>
            <button
              aria-label="Provider bulk-select"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="p-2 rounded text-text-muted hover:text-text-primary hover:bg-grey-100"
            >
              <Menu className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 z-20 min-w-[144px] bg-[var(--surface-read)] border border-[var(--surface-line)] rounded-card shadow-overlay py-1 text-sm"
              >
                <button
                  role="menuitem"
                  disabled={allProvidersSelected}
                  onClick={() => {
                    onProvidersSelectAll();
                    setMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-1.5 text-text-primary enabled:hover:bg-grey-100 disabled:text-text-hint disabled:cursor-not-allowed"
                >
                  Select all
                </button>
                <button
                  role="menuitem"
                  disabled={noProvidersSelected}
                  onClick={() => {
                    onProvidersUnselectAll();
                    setMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-1.5 text-text-primary enabled:hover:bg-grey-100 disabled:text-text-hint disabled:cursor-not-allowed"
                >
                  Unselect all
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
